import { Box, Image } from '@mantine/core';
import { useRevokableObjectUrl } from '../../shared/hooks/useRevokableObjectUrl';
import { itemImageObjectUrl } from './api';

interface Props {
  itemId: string;
  /** The item's elected photo, from `usp_Item_List` since tenant script 057. */
  defaultImageId: string | null | undefined;
  size?: number;
}

/**
 * The item's photo on a list row — legacy shows the elected one on its inquiry screens too.
 *
 * ONE FETCH PER VISIBLE ROW, and that is the deliberate trade. The metadata comes free now that
 * 057 carries `DefaultImageID` on the list, but the bytes still arrive one request at a time,
 * because a page of fifty items carrying its images would be tens of megabytes over a warehouse's
 * connection. The responses are served `immutable` with an ETag equal to the content hash, so the
 * first view of a list costs N requests and every view after it costs nothing.
 *
 * An item with no photo renders an empty box of the same size rather than nothing, so rows do not
 * change height and the column does not jitter as thumbnails arrive.
 */
export function ItemListPhoto({ itemId, defaultImageId, size = 32 }: Props) {
  const url = useRevokableObjectUrl(
    (signal) =>
      defaultImageId
        ? itemImageObjectUrl(itemId, defaultImageId, signal)
        : // Never resolves into a URL, so nothing is fetched and nothing is revoked. Rejecting is
          // how the hook says "render the placeholder".
          Promise.reject(new Error('no photo')),
    [itemId, defaultImageId],
  );

  if (!url) {
    return <Box w={size} h={size} style={{ borderRadius: 4 }} bg="var(--mantine-color-default-hover)" />;
  }

  // alt is empty on purpose: the item number is in the very next column, and a screen reader
  // announcing the same thing twice is worse than not announcing the image.
  return (
    <Image src={url} w={size} h={size} fit="contain" alt="" style={{ borderRadius: 4 }} />
  );
}
